import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Spinner, Button, Row, Col } from 'react-bootstrap'
import * as FaIcons from 'react-icons/fa'
import SimpleMantenedorLayout from '../../components/SimpleMantenedorLayout'
import { useRutasMantenedor } from '../../hooks/useRutasMantenedor'
import RutasTable from '../../components/Rutas/RutasTable'
import RutaModal from '../../components/Rutas/RutaModal'
import CreateRutaModal from '../../components/Rutas/CreateRutaModal'
import SelectModalidadModal from '../../components/Rutas/SelectModalidadModal'
import BulkUploadModal from '../../components/Rutas/BulkUploadModal'
import EditarRutasBulkModal from '../../components/Rutas/EditarRutasBulkModal'
import RecargarGeosortModal from '../../components/Rutas/RecargarGeosortModal'
import { useMantenedores } from '../../context/MantenedoresContext'
import { SuccessSwal, ErrorSwal } from '@helpers/swal'
import Swal from 'sweetalert2'

const RutasConductores = () => {
    const user = useSelector(state => state.auth.user)
    const { userPermissions } = useMantenedores()
    const permisos = userPermissions?.modules?.rutas || {}
    const {
        rutas,
        total,
        loading,
        loadingInitial,
        error,
        pageSize,
        pageNumber,
        searchValue,
        empresaId,
        empresas,
        tmsId,
        tmsList,
        handleSearch,
        handleEmpresaChange,
        handleTmsChange,
        handlePageChange,
        handlePageSizeChange,
        handleUpdateRutaPrice,
        refreshRutas,
        handleCreateRuta,
        handleDownloadPlantilla,
        handleUploadRutasBulk,
        handleDownloadPlantillaEdicion,
        handleUpdateRutasBulk,
        handleReloadGeosort
    } = useRutasMantenedor(user, permisos)

    const [modalOpen, setModalOpen] = useState(false)
    const [selectedRutaId, setSelectedRutaId] = useState(null)
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [selectModalidadOpen, setSelectModalidadOpen] = useState(false)
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false)
    const [downloadingPlantilla, setDownloadingPlantilla] = useState(false)
    const [editarRutasBulkOpen, setEditarRutasBulkOpen] = useState(false)
    const [downloadingPlantillaEdicion, setDownloadingPlantillaEdicion] = useState(false)
    const [geosortModalOpen, setGeosortModalOpen] = useState(false)

    const handleRowClick = ruta => {
        setSelectedRutaId(ruta.id)
        setModalOpen(true)
    }

    const handleCloseModal = () => {
        setModalOpen(false)
        setSelectedRutaId(null)
    }

    const handleRutaUpdated = () => {
        refreshRutas() // Actualizar la tabla
        handleCloseModal() // Cerrar el modal
    }

    // Handlers para modal de selección de modalidad
    const handleOpenSelectModalidad = () => {
        setSelectModalidadOpen(true)
    }

    const handleCloseSelectModalidad = () => {
        setSelectModalidadOpen(false)
    }

    const handleSelectIndividual = () => {
        setSelectModalidadOpen(false)
        setCreateModalOpen(true)
    }

    const handleSelectMasiva = () => {
        setSelectModalidadOpen(false)
        setBulkUploadOpen(true)
    }

    const handleSelectGeosort = () => {
        setSelectModalidadOpen(false)
        setGeosortModalOpen(true)
    }

    // Handlers para modal de creación individual
    const handleCloseCreateModal = () => {
        setCreateModalOpen(false)
    }

    const handleCreateRutaSubmit = async (rutaData) => {
        try {
            const result = await handleCreateRuta(rutaData)
            
            if (result.success) {
                SuccessSwal(
                    'Ruta Creada',
                    result.message || 'La ruta se ha creado exitosamente'
                )
                handleCloseCreateModal()
            } else {
                ErrorSwal(
                    'Error al Crear Ruta',
                    result.error || 'No se pudo crear la ruta'
                )
            }
            
            return result
        } catch (error) {
            ErrorSwal('Error al Crear Ruta', 'Ocurrió un error inesperado al crear la ruta')
            return { success: false, error: 'Error inesperado' }
        }
    }

    // Handlers para modal de carga masiva
    const handleCloseBulkUpload = () => {
        setBulkUploadOpen(false)
    }

    const handleDownloadPlantillaClick = async () => {
        setDownloadingPlantilla(true)
        try {
            const result = await handleDownloadPlantilla()
            
            if (result.success) {
                SuccessSwal(
                    'Plantilla Descargada',
                    result.message || 'La plantilla se ha descargado exitosamente'
                )
            } else {
                ErrorSwal(
                    'Error al Descargar Plantilla',
                    result.error || 'No se pudo descargar la plantilla'
                )
            }
        } catch (error) {
            ErrorSwal('Error al Descargar Plantilla', 'Ocurrió un error inesperado al descargar la plantilla')
        } finally {
            setDownloadingPlantilla(false)
        }
    }

    const handleUploadBulkFile = async (file) => {
        try {
            const result = await handleUploadRutasBulk(file)
            
            if (result.success) {
                // Cerrar modal de carga
                handleCloseBulkUpload()
                
                // Mostrar Swal con información del proceso
                await Swal.fire({
                    icon: 'success',
                    title: 'Proceso de Carga Iniciado',
                    html: `
                        <div style="text-align: left;">
                            <p><strong>El archivo se está procesando de manera asíncrona.</strong></p>
                            <br/>
                            <p><strong>Información del proceso:</strong></p>
                            <ul style="list-style-position: inside;">
                                <li><strong>Rutas a crear:</strong> ${result.totalRutasUnicas}</li>
                                <li><strong>Guías totales:</strong> ${result.totalGuias}</li>
                            </ul>
                            <br/>
                            <p style="color: #856404; background-color: #fff3cd; padding: 10px; border-radius: 5px; border-left: 4px solid #ffc107;">
                                <strong>⏱️ Tiempo estimado:</strong> Entre 10 y 20 minutos dependiendo de la cantidad de rutas.
                            </p>
                            <br/>
                            <p style="color: #004085; background-color: #cce5ff; padding: 10px; border-radius: 5px; border-left: 4px solid #007bff;">
                                <strong>🔔 Notificación:</strong> Será notificado automáticamente una vez finalice el proceso.
                            </p>
                        </div>
                    `,
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#28a745',
                    width: '600px'
                })
            } else {
                // Construir mensaje de error detallado
                let errorHtml = `<div style="text-align: left;">`
                
                // Mensaje principal
                if (result.error) {
                    errorHtml += `<p><strong>${result.error}</strong></p>`
                }
                
                // Mostrar cantidad total de errores y fase
                if (result.total_errores) {
                    errorHtml += `<br/><p style="color: #721c24; background-color: #f8d7da; padding: 10px; border-radius: 5px; border-left: 4px solid #dc3545;">`
                    errorHtml += `<strong>Total de errores encontrados:</strong> ${result.total_errores}`
                    if (result.fase) {
                        errorHtml += `<br/><strong>Fase:</strong> ${result.fase}`
                    }
                    errorHtml += `</p>`
                }
                
                // Mostrar los primeros 3 errores
                if (result.errores && result.errores.length > 0) {
                    errorHtml += `<br/><p><strong>Primeros errores detectados:</strong></p><ul style="list-style-position: inside; margin-left: 10px;">`
                    
                    const errorsToShow = result.errores.slice(0, 3)
                    errorsToShow.forEach(err => {
                        errorHtml += `<li style="margin-bottom: 8px;">`
                        errorHtml += `<strong>Fila ${err.fila}</strong> - `
                        if (err.campo) {
                            errorHtml += `<em>${err.campo}:</em> `
                        }
                        errorHtml += `${err.error}`
                        errorHtml += `</li>`
                    })
                    
                    errorHtml += `</ul>`
                    
                    // Si hay más errores, indicarlo
                    if (result.errores.length > 3) {
                        errorHtml += `<p style="color: #856404; font-style: italic;">... y ${result.errores.length - 3} error(es) adicional(es)</p>`
                    }
                }
                
                errorHtml += `</div>`
                
                // Mostrar Swal con errores detallados
                await Swal.fire({
                    icon: 'error',
                    title: 'Error al Procesar Archivo',
                    html: errorHtml,
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#dc3545',
                    width: '700px'
                })
            }
        } catch (error) {
            ErrorSwal('Error al Cargar Rutas', 'Ocurrió un error inesperado al procesar el archivo')
        }
    }

    // Handlers para modal de edición masiva
    const handleOpenEditarRutasBulk = () => {
        setEditarRutasBulkOpen(true)
    }

    const handleCloseEditarRutasBulk = () => {
        setEditarRutasBulkOpen(false)
    }

    const handleDownloadPlantillaEdicionClick = async () => {
        setDownloadingPlantillaEdicion(true)
        try {
            const result = await handleDownloadPlantillaEdicion()

            if (result.success) {
                SuccessSwal(
                    'Plantilla Descargada',
                    result.message || 'La plantilla se ha descargado exitosamente'
                )
            } else {
                ErrorSwal(
                    'Error al Descargar Plantilla',
                    result.error || 'No se pudo descargar la plantilla'
                )
            }
        } catch (error) {
            ErrorSwal('Error al Descargar Plantilla', 'Ocurrió un error inesperado al descargar la plantilla')
        } finally {
            setDownloadingPlantillaEdicion(false)
        }
    }

    const renderListaErrores = (errores) => {
        let html = `<ul style="list-style-position: inside; margin-left: 10px;">`
        errores.slice(0, 5).forEach(err => {
            html += `<li style="margin-bottom: 6px;"><strong>Fila ${err.fila}</strong>`
            if (err.campo) {
                html += ` - <em>${err.campo}:</em>`
            }
            html += ` ${err.error}</li>`
        })
        html += `</ul>`
        if (errores.length > 5) {
            html += `<p style="color: #856404; font-style: italic;">... y ${errores.length - 5} error(es) adicional(es)</p>`
        }
        return html
    }

    const handleUploadEditarRutasBulkFile = async (file) => {
        try {
            const result = await handleUpdateRutasBulk(file)

            if (result.success) {
                // Cerrar modal de carga
                handleCloseEditarRutasBulk()

                // Mostrar Swal con información del proceso (mismo patrón que la
                // carga masiva de creación: el archivo se procesa en background
                // y se notifica por correo al finalizar, sin polling desde el navegador)
                let html = `<div style="text-align: left;">`
                html += `<p><strong>El archivo se está procesando de manera asíncrona.</strong></p>`
                html += `<br/>`
                html += `<p><strong>Información del proceso:</strong></p>`
                html += `<ul style="list-style-position: inside;">`
                html += `<li><strong>Filas válidas a procesar:</strong> ${result.filasProcesadas}</li>`
                html += `<li><strong>Rutas a actualizar:</strong> ${result.totalOperaciones}</li>`
                html += `<li><strong>Filas omitidas (sin cambios):</strong> ${result.filasOmitidas}</li>`
                html += `</ul>`

                if (result.erroresValidacion && result.erroresValidacion.length > 0) {
                    html += `<br/><p style="color: #856404; background-color: #fff3cd; padding: 10px; border-radius: 5px; border-left: 4px solid #ffc107;">`
                    html += `<strong>Filas con error detectadas en la validación (no se procesarán):</strong> ${result.erroresValidacion.length}</p>`
                    html += renderListaErrores(result.erroresValidacion)
                }

                if (result.advertencias && result.advertencias.length > 0) {
                    html += `<br/><p style="color: #004085; background-color: #cce5ff; padding: 10px; border-radius: 5px; border-left: 4px solid #007bff;">`
                    html += `<strong>Avisos:</strong> ${result.advertencias.length} código(s) de ruta coincidieron con más de una ruta activa (se actualizarán todas).</p>`
                }

                html += `<br/><p style="color: #004085; background-color: #cce5ff; padding: 10px; border-radius: 5px; border-left: 4px solid #007bff;">`
                html += `<strong>🔔 Notificación:</strong> Será notificado por correo una vez finalice el proceso.</p>`
                html += `</div>`

                await Swal.fire({
                    icon: 'success',
                    title: 'Proceso de Edición Iniciado',
                    html,
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#28a745',
                    width: '650px'
                })
            } else {
                let errorHtml = `<div style="text-align: left;">`

                if (result.error) {
                    errorHtml += `<p><strong>${result.error}</strong></p>`
                }

                if (result.errores && result.errores.length > 0) {
                    errorHtml += `<br/><p><strong>Errores detectados:</strong></p>`
                    errorHtml += renderListaErrores(result.errores)
                }
                errorHtml += `</div>`

                await Swal.fire({
                    icon: 'error',
                    title: 'Error al Procesar Archivo',
                    html: errorHtml,
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#dc3545',
                    width: '700px'
                })
            }
        } catch (error) {
            ErrorSwal('Error al Editar Rutas', 'Ocurrió un error inesperado al procesar el archivo')
        }
    }

    // Handlers para modal de recarga de fecha GeoSort
    const handleCloseGeosortModal = () => {
        setGeosortModalOpen(false)
    }

    const handleReloadGeosortSubmit = async (fecha) => {
        try {
            const result = await handleReloadGeosort(fecha)

            if (result.success) {
                handleCloseGeosortModal()

                const totalGuides = result.totalGuides ?? 0
                await Swal.fire({
                    icon: 'success',
                    title: 'Reproceso de GeoSort Iniciado',
                    html: `
                        <div style="text-align: left;">
                            <p><strong>Se reprocesará el día ${result.fecha || fecha}.</strong></p>
                            <br/>
                            <p>Se procesarán aproximadamente <strong>${totalGuides}</strong> guías.</p>
                            <br/>
                            <p style="color: #856404; background-color: #fff3cd; padding: 10px; border-radius: 5px; border-left: 4px solid #ffc107;">
                                <strong>⏱️ Tiempo estimado:</strong> El cambio puede tardar unos 5 minutos en verse reflejado en el sistema.
                            </p>
                        </div>
                    `,
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#28a745',
                    width: '600px'
                })
            } else {
                const tituloPorPaso = {
                    descarga: 'Error al Descargar el Reporte de GeoSort',
                    proceso: 'Error al Iniciar el Procesamiento de GeoSort'
                }
                ErrorSwal(
                    tituloPorPaso[result.step] || 'Error al Recargar GeoSort',
                    result.error || 'No se pudo completar el reproceso de GeoSort'
                )
            }
        } catch (error) {
            ErrorSwal('Error al Recargar GeoSort', 'Ocurrió un error inesperado al recargar la fecha de GeoSort')
        }
    }

    return (
        <SimpleMantenedorLayout
            mantenedorId="rutas"
            title="Mantenedor de Rutas"
            subtitle="Gestión de rutas asignadas a conductores"
            iconColor="#007bff"
            showBackButton={false}
            breadcrumbs={[
                {
                    label: 'Rutas'
                    // path: '/mantenedores/rutas'
                }
            ]}>
            {error && <div className="alert alert-danger mb-2">{error}</div>}
            
            {/* Encabezado con botón de crear ruta */}
            {permisos.canEdit && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 style={{ color: '#212529', marginBottom: '8px' }}>Gestión de Rutas</h4>
                                <p style={{ color: '#6c757d', marginBottom: '0' }}>
                                    Administra las rutas asignadas a conductores
                                </p>
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-primary"
                                    onClick={handleOpenEditarRutasBulk}
                                    disabled={loading}
                                >
                                    <FaIcons.FaFileExcel className="me-2" />
                                    Edición Masiva
                                </Button>
                                <Button
                                    variant="success"
                                    onClick={handleOpenSelectModalidad}
                                    disabled={loading}
                                >
                                    <FaIcons.FaPlus className="me-2" />
                                    Nueva Ruta
                                </Button>
                            </div>
                        </div>
                    </Col>
                </Row>
            )}
            
            <RutasTable
                data={rutas}
                loading={loading}
                total={total}
                pageSize={pageSize}
                pageNumber={pageNumber}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onRowClick={handleRowClick}
                searchValue={searchValue}
                onSearch={handleSearch}
                empresas={empresas}
                empresaId={empresaId}
                onEmpresaChange={handleEmpresaChange}
                tmsList={tmsList}
                tmsId={tmsId}
                onTmsChange={handleTmsChange}
            />
            
            <RutaModal 
                show={modalOpen} 
                onHide={handleCloseModal} 
                rutaId={selectedRutaId}
                handleUpdateRutaPrice={handleUpdateRutaPrice}
                onRutaUpdated={handleRutaUpdated}
                permisos={permisos}
            />

            {/* Modal de selección de modalidad */}
            <SelectModalidadModal
                show={selectModalidadOpen}
                onHide={handleCloseSelectModalidad}
                onSelectIndividual={handleSelectIndividual}
                onSelectMasiva={handleSelectMasiva}
                onSelectGeosort={handleSelectGeosort}
            />

            {/* Modal para crear nueva ruta individual */}
            <CreateRutaModal
                show={createModalOpen}
                onHide={handleCloseCreateModal}
                onCreateRuta={handleCreateRutaSubmit}
                user={user}
                loading={loading}
                permisos={permisos}
            />

            {/* Modal para carga masiva de rutas */}
            <BulkUploadModal
                show={bulkUploadOpen}
                onHide={handleCloseBulkUpload}
                onDownloadPlantilla={handleDownloadPlantillaClick}
                onUploadFile={handleUploadBulkFile}
                loading={loading}
                downloadingPlantilla={downloadingPlantilla}
            />

            {/* Modal para edición masiva de rutas */}
            <EditarRutasBulkModal
                show={editarRutasBulkOpen}
                onHide={handleCloseEditarRutasBulk}
                onDownloadPlantilla={handleDownloadPlantillaEdicionClick}
                onUploadFile={handleUploadEditarRutasBulkFile}
                loading={loading}
                downloadingPlantilla={downloadingPlantillaEdicion}
            />

            {/* Modal para recargar/reprocesar una fecha de GeoSort */}
            <RecargarGeosortModal
                show={geosortModalOpen}
                onHide={handleCloseGeosortModal}
                onReload={handleReloadGeosortSubmit}
                loading={loading}
            />
        </SimpleMantenedorLayout>
    )
}

export default RutasConductores
